/**
 * Admin editor for the Marketing Copilot's persona (PR 7, spec §2.4b).
 *
 * Rides the pre-existing `GET/PUT /api/marketing/copilot/config` endpoints
 * (`marketing:configure`), which shipped without any UI. The persona is an
 * embedded config on the single global CopilotConfig row — not a `Persona`
 * (locked decision §4.1) — so this edits four fields and nothing else.
 *
 * Voice picking reuses the shared voice browser (`VoiceIdField`), the same
 * component the persona editor and the Doc assistant settings use.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui'
import { VoiceIdField } from '@/components/voice-browser/VoiceIdField'
import { apiClient } from '@/services/apiClient'
import { usePermission } from '@/hooks/usePermission'
import { useSettingsStore } from '@/stores'

interface PersonaVoice {
  provider: 'elevenlabs' | 'azure' | 'kokoro'
  voice_id: string
  voice_name?: string
  voice_preview_url?: string
  model_id?: string
}

interface PersonaBlock {
  name: string | null
  avatar_url: string | null
  voice_config: PersonaVoice | null
  system_prompt: string | null
}

interface ConfigResponse {
  config?: Record<string, unknown>
  persona?: Partial<PersonaBlock>
}

const EMPTY: PersonaBlock = {
  name: null,
  avatar_url: null,
  voice_config: null,
  system_prompt: null,
}

/** Blank/whitespace-only input means "clear this override", i.e. send null. */
function orNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

export function CopilotPersonaSettings() {
  const canConfigure = usePermission('marketing:configure')
  const hasElevenLabsKey = useSettingsStore((s) => s.hasElevenLabsKey)

  const [persona, setPersona] = useState<PersonaBlock>(EMPTY)
  const [voiceEnabled, setVoiceEnabled] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const apply = useCallback((data: ConfigResponse) => {
    setPersona({ ...EMPTY, ...(data.persona ?? {}) })
    setVoiceEnabled(data.config?.enable_voice !== false)
  }, [])

  useEffect(() => {
    if (!canConfigure) return
    apiClient
      .get<ConfigResponse>('/api/marketing/copilot/config')
      .then(apply)
      .catch(() => setError('Could not load the copilot configuration.'))
  }, [canConfigure, apply])

  const save = async () => {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const updated = await apiClient.put<ConfigResponse>(
        '/api/marketing/copilot/config',
        { persona },
      )
      apply(updated)
      setSaved(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the copilot persona.')
    } finally {
      setSaving(false)
    }
  }

  if (!canConfigure) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Copilot persona</CardTitle>
        <CardDescription>
          Name, face, voice and instructions for the marketing copilot. Leave a
          field blank to fall back to the built-in default.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 max-w-xl">
        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <label className="block">
          <span className="text-sm font-medium">Copilot name</span>
          <Input
            aria-label="Copilot name"
            className="mt-1"
            placeholder="Marketing Copilot"
            value={persona.name ?? ''}
            onChange={(e) =>
              setPersona((p) => ({ ...p, name: orNull(e.target.value) }))
            }
          />
          <span className="text-xs text-muted-foreground">
            Shown in the panel header and told to the model, so it can introduce
            itself by name.
          </span>
        </label>

        <label className="block">
          <span className="text-sm font-medium">Avatar URL</span>
          <Input
            aria-label="Avatar URL"
            className="mt-1"
            placeholder="https://…"
            value={persona.avatar_url ?? ''}
            onChange={(e) =>
              setPersona((p) => ({ ...p, avatar_url: orNull(e.target.value) }))
            }
          />
          <span className="text-xs text-muted-foreground">
            Falls back to the default glyph. Needs a name to be used, so the
            image always has a label.
          </span>
        </label>

        <label className="block">
          <span className="text-sm font-medium" id="copilot-system-prompt-label">
            System prompt
          </span>
          <textarea
            aria-labelledby="copilot-system-prompt-label"
            aria-label="System prompt"
            rows={8}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
            placeholder="Leave blank to use the built-in marketing specialist prompt."
            value={persona.system_prompt ?? ''}
            onChange={(e) =>
              setPersona((p) => ({ ...p, system_prompt: orNull(e.target.value) }))
            }
          />
          <span className="text-xs text-muted-foreground">
            Replaces the built-in prompt entirely. Include{' '}
            <code>{'{context}'}</code> where the live marketing state should go —
            if you omit it, the state is appended at the end.
          </span>
        </label>

        <div className="border-t border-border pt-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Voice</h3>
            <p className="text-xs text-muted-foreground">
              The voice the copilot speaks its replies in. Users still need an
              ElevenLabs credential of their own.
            </p>
          </div>

          {!voiceEnabled && (
            <p className="text-xs text-warning-strong">
              Copilot voice is turned off for this deployment — a voice
              configured here stays silent until it is re-enabled.
            </p>
          )}

          <VoiceIdField
            provider={persona.voice_config?.provider ?? 'elevenlabs'}
            voiceId={persona.voice_config?.voice_id ?? ''}
            voiceName={persona.voice_config?.voice_name}
            voicePreviewUrl={persona.voice_config?.voice_preview_url}
            hasApiKey={hasElevenLabsKey}
            onChange={(voiceId, voiceName, voicePreviewUrl, supportedModels) =>
              setPersona((p) => ({
                ...p,
                voice_config: {
                  provider: 'elevenlabs',
                  ...p.voice_config,
                  voice_id: voiceId,
                  voice_name: voiceName,
                  voice_preview_url: voicePreviewUrl,
                  model_id: supportedModels?.[0] ?? p.voice_config?.model_id,
                },
              }))
            }
          />

          {persona.voice_config && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              onClick={() => setPersona((p) => ({ ...p, voice_config: null }))}
            >
              Clear voice
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save persona'}
          </Button>
          {saved && !error && (
            <span className="text-xs text-muted-foreground">Saved.</span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
